import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { 
  AlertTriangle, Clock, CheckCircle, XCircle, Search, RotateCcw, MapPin, Calendar as CalendarIcon, 
  User, Users, FileText, Building2, Filter, ChevronRight, Eye, History, 
  AlertCircle, Loader2, MapPinOff, Timer, ArrowUp, ArrowRight, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE CONFIGURATIONS
// ═══════════════════════════════════════════════════════════════════════════════

const PRIORITY_BADGE = {
  high: { label: 'High', className: 'bg-red-600 text-white', icon: AlertTriangle },
  medium: { label: 'Medium', className: 'bg-yellow-500 text-black', icon: AlertCircle },
  low: { label: 'Low', className: 'bg-blue-500 text-white', icon: Clock },
};

const EXCEPTION_TYPE_BADGE = {
  missing_both_clocks: { label: 'Missing Both', className: 'bg-red-700 text-white' },
  missing_clock_out: { label: 'Missing Clock Out', className: 'bg-red-500 text-white' },
  missing_clock_in: { label: 'Missing Clock In', className: 'bg-red-500 text-white' },
  late_sign_in: { label: 'Late Sign In', className: 'bg-yellow-500 text-black' },
  early_sign_out: { label: 'Early Sign Out', className: 'bg-orange-500 text-white' },
  late_and_early: { label: 'Late & Early', className: 'bg-orange-600 text-white' },
  overtime: { label: 'Overtime', className: 'bg-blue-500 text-white' },
  gps_mismatch: { label: 'GPS Issue', className: 'bg-purple-500 text-white' },
  returned_for_correction: { label: 'Returned', className: 'bg-orange-500 text-white' },
  attendance_exception: { label: 'Exception', className: 'bg-red-500 text-white' },
  pending_correction: { label: 'Pending Correction', className: 'bg-orange-500 text-white' },
  no_attendance: { label: 'No Attendance', className: 'bg-red-700 text-white' },
  incomplete_shift: { label: 'Incomplete Shift', className: 'bg-red-500 text-white' },
  missing_start: { label: 'Missing Start', className: 'bg-red-500 text-white' },
  stale_pending: { label: 'Stale Pending', className: 'bg-gray-500 text-white' },
  pending_review: { label: 'Pending Review', className: 'bg-yellow-500 text-black' },
};

const ATTENDANCE_BADGE = {
  on_time: { label: 'On Time', className: 'bg-green-500 text-white' },
  late_sign_in: { label: 'Late Sign In', className: 'bg-yellow-500 text-black' },
  early_sign_out: { label: 'Early Sign Out', className: 'bg-yellow-500 text-black' },
  late_and_early: { label: 'Late & Early', className: 'bg-orange-500 text-white' },
  missing_clock_in: { label: 'Missing Clock In', className: 'bg-red-500 text-white' },
  missing_clock_out: { label: 'Missing Clock Out', className: 'bg-red-500 text-white' },
  missing_both_clocks: { label: 'No Attendance', className: 'bg-red-700 text-white' },
  overtime: { label: 'Overtime', className: 'bg-blue-500 text-white' },
  attendance_exception: { label: 'Exception', className: 'bg-red-500 text-white' },
};

const APPROVAL_BADGE = {
  pending_approval: { label: 'Pending', className: 'bg-yellow-500 text-black' },
  approved_scheduled: { label: 'Approved', className: 'bg-green-600 text-white' },
  approved_adjusted: { label: 'Approved (Adj)', className: 'bg-green-500 text-white' },
  rejected: { label: 'Rejected', className: 'bg-red-600 text-white' },
  returned_for_correction: { label: 'Returned', className: 'bg-orange-500 text-white' },
  DRAFT: { label: 'Draft', className: 'bg-gray-500 text-white' },
  SUBMITTED: { label: 'Submitted', className: 'bg-yellow-500 text-black' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const formatTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleString('en-AU', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit' 
    });
  } catch {
    return isoString;
  }
};

const formatMinutes = (mins) => {
  if (!mins && mins !== 0) return '-';
  const h = Math.floor(Math.abs(mins) / 60);
  const m = Math.abs(mins) % 60;
  const sign = mins < 0 ? '-' : '';
  if (h > 0) return `${sign}${h}h ${m}m`;
  return `${sign}${m}m`;
};

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const PriorityBadge = ({ priority, escalated }) => {
  const config = PRIORITY_BADGE[priority] || { label: priority || 'Unknown', className: 'bg-gray-500 text-white', icon: Clock };
  const Icon = config.icon;
  return (
    <div className="flex items-center gap-1">
      <Badge className={`${config.className} text-xs flex items-center gap-1`} data-testid="priority-badge">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
      {escalated && (
        <ArrowUp className="h-3 w-3 text-red-500" title="Priority escalated due to age" />
      )}
    </div>
  );
};

const ExceptionTypeBadge = ({ type }) => {
  const config = EXCEPTION_TYPE_BADGE[type] || { label: type || 'Unknown', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="exception-type-badge">
      {config.label}
    </Badge>
  );
};

const AttendanceBadge = ({ status }) => {
  const config = ATTENDANCE_BADGE[status] || { label: status || '-', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="attendance-badge">
      {config.label}
    </Badge>
  );
};

const ApprovalBadge = ({ status }) => {
  const config = APPROVAL_BADGE[status] || { label: status || '-', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="approval-badge">
      {config.label}
    </Badge>
  );
};

const AgeBadge = ({ days }) => {
  let className = 'bg-green-500 text-white';
  if (days > 7) className = 'bg-red-500 text-white';
  else if (days > 3) className = 'bg-orange-500 text-white';
  else if (days > 1) className = 'bg-yellow-500 text-black';
  
  return (
    <Badge className={`${className} text-xs font-mono`} data-testid="age-badge">
      {days}d
    </Badge>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DATE RANGE PICKER
// ═══════════════════════════════════════════════════════════════════════════════

const DateRangePicker = ({ dateFrom, dateTo, onDateFromChange, onDateToChange }) => {
  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-[130px] justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateFrom ? format(new Date(dateFrom), 'dd MMM') : 'From'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateFrom ? new Date(dateFrom) : undefined}
            onSelect={(date) => onDateFromChange(date ? format(date, 'yyyy-MM-dd') : '')}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <span className="text-muted-foreground">-</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-[130px] justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateTo ? format(new Date(dateTo), 'dd MMM') : 'To'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateTo ? new Date(dateTo) : undefined}
            onSelect={(date) => onDateToChange(date ? format(date, 'yyyy-MM-dd') : '')}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {(dateFrom || dateTo) && (
        <Button variant="ghost" size="sm" onClick={() => { onDateFromChange(''); onDateToChange(''); }}>
          Clear
        </Button>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EXCEPTION DETAIL DRAWER
// ═══════════════════════════════════════════════════════════════════════════════

const ExceptionDetailDrawer = ({ exception, open, onClose, onOpenApproval }) => {
  if (!exception) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="exception-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Exception Details
          </SheetTitle>
          <SheetDescription>
            {exception.date} • {exception.staff_name}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Section A: Exception Summary */}
          <Card data-testid="exception-summary-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Exception Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Exception Type</span>
                <ExceptionTypeBadge type={exception.exception_type} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Priority</span>
                <PriorityBadge priority={exception.priority} escalated={exception.escalated} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Age</span>
                <AgeBadge days={exception.age_days || 0} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Review Status</span>
                <ApprovalBadge status={exception.approval_status || exception.status} />
              </div>
              
              {/* Why flagged */}
              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>Why flagged:</strong>{' '}
                  {exception.exception_type === 'missing_both_clocks' && 'No clock-in or clock-out recorded for this shift.'}
                  {exception.exception_type === 'missing_clock_out' && 'Staff clocked in but no clock-out was recorded.'}
                  {exception.exception_type === 'missing_clock_in' && 'Staff clocked out but no clock-in was recorded.'}
                  {exception.exception_type === 'late_sign_in' && `Staff signed in ${exception.late_minutes || 0} minutes late.`}
                  {exception.exception_type === 'early_sign_out' && `Staff signed out ${exception.early_leave_minutes || 0} minutes early.`}
                  {exception.exception_type === 'overtime' && `Staff worked ${exception.overtime_minutes || 0} minutes overtime.`}
                  {exception.exception_type === 'returned_for_correction' && 'Timesheet was returned by reviewer for correction.'}
                  {exception.exception_type === 'gps_mismatch' && 'GPS location did not match expected work location.'}
                  {!['missing_both_clocks', 'missing_clock_out', 'missing_clock_in', 'late_sign_in', 'early_sign_out', 'overtime', 'returned_for_correction', 'gps_mismatch'].includes(exception.exception_type) && 'This record has an attendance exception that requires review.'}
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Section B: Shift & Attendance */}
          <Card data-testid="shift-attendance-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4" /> Shift & Attendance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground text-xs">Scheduled Start</p>
                  <p className="font-mono">{formatTime(exception.base_start_time) || exception.start_time || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Scheduled End</p>
                  <p className="font-mono">{formatTime(exception.base_end_time) || exception.end_time || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Actual Clock In</p>
                  <p className="font-mono">{formatTime(exception.actual_clock_in_time) || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Actual Clock Out</p>
                  <p className="font-mono">{formatTime(exception.actual_clock_out_time) || '-'}</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Attendance Status</span>
                <AttendanceBadge status={exception.attendance_status} />
              </div>

              {/* Attendance flags */}
              <div className="flex flex-wrap gap-2">
                {exception.late_minutes > 0 && (
                  <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50">
                    Late: {exception.late_minutes}m
                  </Badge>
                )}
                {exception.early_leave_minutes > 0 && (
                  <Badge variant="outline" className="text-orange-700 border-orange-300 bg-orange-50">
                    Early: {exception.early_leave_minutes}m
                  </Badge>
                )}
                {exception.overtime_minutes > 0 && (
                  <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                    OT: {exception.overtime_minutes}m
                  </Badge>
                )}
                {exception.gps_mismatch && (
                  <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50">
                    <MapPinOff className="h-3 w-3 mr-1" /> GPS Issue
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section C: Support Context */}
          <Card data-testid="support-context-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="h-4 w-4" /> Support Context
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground text-xs">Staff</p>
                  <p className="font-medium">{exception.staff_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Participant</p>
                  <p className="font-medium">{exception.participant_name || '-'}</p>
                </div>
              </div>

              {exception.linked_participants && exception.linked_participants.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground text-xs mb-1">
                    <Users className="h-3 w-3 inline mr-1" />
                    Linked Participants ({exception.linked_participants.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {exception.linked_participants.map((p, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {p.name || p}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {exception.house_name && (
                <div>
                  <p className="text-muted-foreground text-xs">House (SIL)</p>
                  <p>{exception.house_name}</p>
                </div>
              )}

              {exception.support_type && (
                <div>
                  <p className="text-muted-foreground text-xs">Support Type</p>
                  <p>{exception.support_type}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section D: Approval / Correction Status */}
          <Card data-testid="approval-status-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Approval Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Current Status</span>
                <ApprovalBadge status={exception.approval_status || exception.status} />
              </div>
              
              {exception.correction_notes && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground text-xs">Correction Notes</p>
                  <p className="bg-orange-50 rounded p-2 text-xs text-orange-800">{exception.correction_notes}</p>
                </div>
              )}

              {exception.is_superseded && (
                <Alert className="border-gray-300 bg-gray-50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-gray-700">
                    This timesheet has been superseded.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Section E: Notes / Reasons */}
          {(exception.staff_notes || exception.notes || exception.exception_reason || exception.rejection_reason) && (
            <Card data-testid="notes-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Notes & Reasons
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {(exception.staff_notes || exception.notes) && (
                  <div>
                    <p className="text-muted-foreground text-xs">Staff Notes</p>
                    <p className="bg-muted/50 rounded p-2 text-xs">{exception.staff_notes || exception.notes}</p>
                  </div>
                )}
                {exception.exception_reason && (
                  <div>
                    <p className="text-muted-foreground text-xs">Exception Reason</p>
                    <p className="bg-orange-50 rounded p-2 text-xs text-orange-800">{exception.exception_reason}</p>
                  </div>
                )}
                {exception.rejection_reason && (
                  <div>
                    <p className="text-muted-foreground text-xs">Rejection Reason</p>
                    <p className="bg-red-50 rounded p-2 text-xs text-red-800">{exception.rejection_reason}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Section F: Audit Summary */}
          <Card data-testid="audit-summary-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <History className="h-4 w-4" /> Audit Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {exception.updated_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span className="font-mono text-xs">{formatDateTime(exception.updated_at)}</span>
                </div>
              )}
              {exception.created_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="font-mono text-xs">{formatDateTime(exception.created_at)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="border-primary/20" data-testid="actions-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full" 
                onClick={() => onOpenApproval(exception)}
                data-testid="open-approval-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in Approval Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function ExceptionQueuePage() {
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('queue');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Queue data
  const [queueItems, setQueueItems] = useState([]);
  const [queueStats, setQueueStats] = useState({ total: 0, high: 0, medium: 0, low: 0 });
  
  // Follow-up data
  const [followUpItems, setFollowUpItems] = useState([]);
  const [followUpStats, setFollowUpStats] = useState({ total: 0, by_issue_type: {} });
  
  // Filters
  const [priorityFilter, setPriorityFilter] = useState('');
  const [exceptionTypeFilter, setExceptionTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minAgeDays, setMinAgeDays] = useState('3');
  
  // Drawer
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Check permission
  const hasPermission = permissions?.includes('timesheets.approve') || 
    ['Super Admin', 'Admin', 'HR', 'Accounts', 'Payroll Manager'].includes(user?.role_name);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (priorityFilter) params.append('priority', priorityFilter);
      if (exceptionTypeFilter) params.append('exception_type', exceptionTypeFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const url = `/timesheets/v2/exception-queue${params.toString() ? '?' + params.toString() : ''}`;
      const res = await api.get(url);
      setQueueItems(res.data.items || []);
      setQueueStats(res.data.stats || { total: 0, high: 0, medium: 0, low: 0 });
    } catch (e) {
      toast.error('Failed to load exception queue');
    } finally {
      setLoading(false);
    }
  }, [priorityFilter, exceptionTypeFilter, dateFrom, dateTo]);

  const fetchFollowUp = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('min_age_days', minAgeDays || '3');
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const res = await api.get(`/timesheets/v2/follow-up?${params.toString()}`);
      setFollowUpItems(res.data.items || []);
      setFollowUpStats(res.data.stats || { total: 0, by_issue_type: {} });
    } catch (e) {
      console.error('Failed to load follow-up queue', e);
    }
  }, [minAgeDays, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === 'queue') {
      fetchQueue();
    } else {
      fetchFollowUp();
    }
  }, [activeTab, fetchQueue, fetchFollowUp]);

  const filteredItems = (activeTab === 'queue' ? queueItems : followUpItems).filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      item.staff_name?.toLowerCase().includes(s) ||
      item.participant_name?.toLowerCase().includes(s) ||
      item.date?.includes(s) ||
      item.house_name?.toLowerCase().includes(s)
    );
  });

  const handleRowClick = (item) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const handleOpenApproval = (item) => {
    setDrawerOpen(false);
    navigate(`/timesheets/approval?highlight=${item.id}`);
  };

  const clearFilters = () => {
    setPriorityFilter('');
    setExceptionTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setMinAgeDays('3');
  };

  const hasActiveFilters = priorityFilter || exceptionTypeFilter || dateFrom || dateTo;

  // Permission check
  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              You do not have permission to access the exception queue.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="exception-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Exception Queue</h1>
          <p className="text-muted-foreground mt-1">Review and resolve attendance exceptions</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={cn(hasActiveFilters && 'border-primary text-primary')}
          data-testid="toggle-filters"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && <Badge className="ml-2 h-5 w-5 p-0 justify-center">!</Badge>}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-red-500 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => { setPriorityFilter('high'); setActiveTab('queue'); }}
              data-testid="stats-high">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">High Priority</p>
                <p className="text-2xl font-bold">{queueStats.high || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-500 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => { setPriorityFilter('medium'); setActiveTab('queue'); }}
              data-testid="stats-medium">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Medium Priority</p>
                <p className="text-2xl font-bold">{queueStats.medium || 0}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-500 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => { setPriorityFilter('low'); setActiveTab('queue'); }}
              data-testid="stats-low">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Low Priority</p>
                <p className="text-2xl font-bold">{queueStats.low || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-500 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setActiveTab('follow-up')}
              data-testid="stats-follow-up">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Follow-Up Needed</p>
                <p className="text-2xl font-bold">{followUpStats.total || 0}</p>
              </div>
              <RotateCcw className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-dashed" data-testid="filters-panel">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Date Range</Label>
                <DateRangePicker
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={setDateFrom}
                  onDateToChange={setDateTo}
                />
              </div>
              
              {activeTab === 'queue' && (
                <>
                  <div className="space-y-1 min-w-[150px]">
                    <Label className="text-xs">Priority</Label>
                    <Select value={priorityFilter || "all"} onValueChange={(val) => setPriorityFilter(val === "all" ? "" : val)}>
                      <SelectTrigger data-testid="priority-filter">
                        <SelectValue placeholder="All Priorities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1 min-w-[180px]">
                    <Label className="text-xs">Exception Type</Label>
                    <Select value={exceptionTypeFilter || "all"} onValueChange={(val) => setExceptionTypeFilter(val === "all" ? "" : val)}>
                      <SelectTrigger data-testid="exception-type-filter">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="missing_both_clocks">Missing Both</SelectItem>
                        <SelectItem value="missing_clock_out">Missing Clock Out</SelectItem>
                        <SelectItem value="missing_clock_in">Missing Clock In</SelectItem>
                        <SelectItem value="late_sign_in">Late Sign In</SelectItem>
                        <SelectItem value="early_sign_out">Early Sign Out</SelectItem>
                        <SelectItem value="overtime">Overtime</SelectItem>
                        <SelectItem value="returned_for_correction">Returned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {activeTab === 'follow-up' && (
                <div className="space-y-1 min-w-[150px]">
                  <Label className="text-xs">Min Age (Days)</Label>
                  <Select value={minAgeDays} onValueChange={setMinAgeDays}>
                    <SelectTrigger data-testid="min-age-filter">
                      <SelectValue placeholder="3 days" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1+ days</SelectItem>
                      <SelectItem value="3">3+ days</SelectItem>
                      <SelectItem value="7">7+ days</SelectItem>
                      <SelectItem value="14">14+ days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
                  Clear All
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="queue" data-testid="tab-queue">
              Exception Queue ({queueStats.total || 0})
            </TabsTrigger>
            <TabsTrigger value="follow-up" data-testid="tab-follow-up">
              Follow-Up ({followUpStats.total || 0})
            </TabsTrigger>
          </TabsList>

          <div className="relative w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search staff, participant..." 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              className="pl-9"
              data-testid="exception-search"
            />
          </div>
        </div>

        {/* Queue Tab Content */}
        <TabsContent value="queue">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Priority</TableHead>
                    <TableHead>Exception</TableHead>
                    <TableHead className="w-[50px]">Age</TableHead>
                    <TableHead className="w-[100px]">Date</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Participant</TableHead>
                    <TableHead className="text-center">Scheduled</TableHead>
                    <TableHead>Attendance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                        No exceptions found
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.map(item => (
                    <TableRow 
                      key={item.id} 
                      data-testid={`exception-row-${item.id}`} 
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleRowClick(item)}
                    >
                      <TableCell>
                        <PriorityBadge priority={item.priority} escalated={item.escalated} />
                      </TableCell>
                      <TableCell>
                        <ExceptionTypeBadge type={item.exception_type} />
                      </TableCell>
                      <TableCell>
                        <AgeBadge days={item.age_days || 0} />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{item.date}</TableCell>
                      <TableCell className="font-medium">{item.staff_name || 'N/A'}</TableCell>
                      <TableCell>{item.participant_name || 'N/A'}</TableCell>
                      <TableCell className="text-center font-mono">
                        {(item.base_duration_hours || item.hours_worked || 0).toFixed(2)}h
                      </TableCell>
                      <TableCell>
                        <AttendanceBadge status={item.attendance_status} />
                      </TableCell>
                      <TableCell>
                        <ApprovalBadge status={item.approval_status || item.status} />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Follow-Up Tab Content */}
        <TabsContent value="follow-up">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Issue Type</TableHead>
                    <TableHead className="w-[50px]">Age</TableHead>
                    <TableHead className="w-[100px]">Date</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Participant</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Recommended Action</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No follow-up items found
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.map(item => (
                    <TableRow 
                      key={item.id} 
                      data-testid={`follow-up-row-${item.id}`} 
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleRowClick(item)}
                    >
                      <TableCell>
                        <ExceptionTypeBadge type={item.issue_type} />
                      </TableCell>
                      <TableCell>
                        <AgeBadge days={item.age_days || 0} />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{item.date}</TableCell>
                      <TableCell className="font-medium">{item.staff_name || 'N/A'}</TableCell>
                      <TableCell>{item.participant_name || 'N/A'}</TableCell>
                      <TableCell>
                        <ApprovalBadge status={item.approval_status || item.status} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {item.recommended_action}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Drawer */}
      <ExceptionDetailDrawer
        exception={selectedItem}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpenApproval={handleOpenApproval}
      />
    </div>
  );
}
